import React, { useState } from "react";
import {
  FaCalendarAlt,
  FaMedal,
  FaUserTie,
  FaUniversity,
  FaChevronDown,
  FaChevronUp,
  FaSpinner,
} from "react-icons/fa";
import {
  useGetIupcTeamsQuery,
  useGetPastIupcQuery,
} from "../../features/api/appApi";

const PastIUPCPage = () => {
  const { data: pastContests = [], isLoading: loading } = useGetPastIupcQuery();
  const [expandedContest, setExpandedContest] = useState(null);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return isNaN(date)
      ? "Invalid date"
      : date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });
  };

  return (
    <main className="home-shell min-h-screen px-4 pb-14 pt-28 sm:px-6">
      <section className="home-section border-none px-0 py-0">
        <div className="home-container">
          <div className="home-card-strong p-8 sm:p-10">
            <span className="section-kicker">IUPC / ICPC</span>
            <h1 className="home-title mt-4 text-3xl font-bold sm:text-5xl">
              Past <span className="home-accent-gradient">Contests</span>
            </h1>
            <p className="mt-4 max-w-3xl text-sm sm:text-base home-muted">
              Browse previous contests and review GUB teams, ranks, solved counts, and coach information.
            </p>
          </div>
        </div>
      </section>

      <section className="home-section border-none px-0 pt-8">
        <div className="home-container">
          {loading ? (
            <div className="home-card flex h-52 items-center justify-center">
              <FaSpinner className="animate-spin text-3xl home-accent" />
            </div>
          ) : (
            <div className="space-y-4 home-stagger-stack">
              {pastContests.map((contest) => (
                <ContestCard
                  key={contest.id}
                  contest={contest}
                  isExpanded={expandedContest === contest.id}
                  onToggle={() =>
                    setExpandedContest((prev) =>
                      prev === contest.id ? null : contest.id
                    )
                  }
                  formatDate={formatDate}
                />
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

const ContestCard = ({ contest, isExpanded, onToggle, formatDate }) => {
  const { data: teams = [], isFetching: teamsLoading } = useGetIupcTeamsQuery(
    contest.id,
    {
      skip: !isExpanded,
    }
  );

  return (
    <article className="home-card overflow-hidden">
      <button
        type="button"
        className="w-full p-5 text-left"
        onClick={onToggle}
        style={{ borderBottom: isExpanded ? "1px solid var(--border)" : "none" }}
      >
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-xl font-bold sm:text-2xl">{contest.name}</h2>
            <p className="mt-2 inline-flex items-center gap-2 text-sm home-muted">
              <FaCalendarAlt className="home-accent" /> {formatDate(contest.contestDate)}
            </p>
          </div>
          <span className="home-pill inline-flex items-center gap-2 px-3 py-1 text-xs font-semibold">
            {isExpanded ? <FaChevronUp /> : <FaChevronDown />} Teams
          </span>
        </div>
      </button>

      {isExpanded ? (
        <div className="p-5">
          <h3 className="mb-4 inline-flex items-center gap-2 text-base font-semibold">
            <FaUniversity className="home-accent" /> Participating GUB Teams
          </h3>

          {teamsLoading ? (
            <div className="py-6 text-center">
              <FaSpinner className="mx-auto animate-spin text-2xl home-accent" />
            </div>
          ) : teams.length === 0 ? (
            <div className="home-card-strong p-5 text-sm home-muted">
              No teams found for this contest.
            </div>
          ) : (
            <div className="space-y-3">
              {teams.map((team) => (
                <TeamCard key={team.id} team={team} />
              ))}
            </div>
          )}
        </div>
      ) : null}
    </article>
  );
};

const TeamCard = ({ team }) => {
  return (
    <div className="home-card-strong p-4">
      <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <h4 className="text-lg font-semibold">{team.name}</h4>
        <div className="flex flex-wrap gap-2 text-xs font-semibold">
          <span className="home-pill px-2.5 py-1">
            <FaMedal className="mr-1 inline home-accent" /> Rank {team.rank}
          </span>
          <span className="home-pill px-2.5 py-1">Solved {team.solved}</span>
        </div>
      </div>

      <div className="grid home-stagger-grid gap-2 sm:grid-cols-3">
        {[team.member1, team.member2, team.member3].map((member, idx) => (
          <div key={idx} className="home-card px-3 py-2 text-sm">
            {member}
          </div>
        ))}
      </div>

      <p className="mt-3 inline-flex items-center gap-2 text-sm home-muted">
        <FaUserTie className="home-accent" /> Coach: {team.coach}
      </p>
    </div>
  );
};

export default PastIUPCPage;
